import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FsCustomerService, FsErrorResponse } from '@fairandsmart/angular';
import { Customer, PaymentSource, ShippingDetails } from '@fairandsmart/types';
import { Store } from '@ngrx/store';
import {
  EMPTY, Observable, of, throwError,
} from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { AlertService } from '@Services/alert.service';
import { OrgaService } from '@Services/orga.service';
import { FsOrgState } from '../../../reducers';
import { PaymentSourceAdded, PaymentSourceRemoved, ShippingDetailsUpdated } from './configuration.actions';
import {
  AddPaymentDialogComponent,
  AddPaymentDialogComponentData,
} from './offer/add-payment-dialog/add-payment-dialog.component';
import {
  UpdateShippingInfosDialogComponent,
  UpdateShippingInfosDialogComponentData,
} from './offer/update-shipping-infos-dialog/update-shipping-infos-dialog.component';

@Injectable()
export class CustomerService {
  private orgaService = inject(OrgaService);

  private fsCustomerService = inject(FsCustomerService);

  private dialog = inject(MatDialog);

  private store = inject<Store<FsOrgState>>(Store);

  private alert = inject(AlertService);

  getCustomer(): Observable<Customer> {
    return this.fsCustomerService.getCustomer(this.orgaService.customerId);
  }

  getPaymentSource(): Observable<PaymentSource> {
    return this.fsCustomerService.getPaymentSource(this.orgaService.customerId);
  }

  removePaymentSourcePrompt(): Observable<void | never> {
    return this.alert.promptConfirmDialog({
      data: {
        title: 'CURRENT_OFFERS.PAYMENT.REMOVE.TITLE',
        content: 'CURRENT_OFFERS.PAYMENT.REMOVE.MESSAGE',
        confirm: 'COMMON.BUTTONS.DELETE',
      },
    }).afterClosed().pipe(
      mergeMap((confirmed: boolean) => {
        if (confirmed) {
          return this.fsCustomerService.removePaymentSource(this.orgaService.customerId);
        }
        return EMPTY;
      }),
      tap(() => {
        this.alert.success('CURRENT_OFFERS.PAYMENT.REMOVE.SUCCESS');
        this.store.dispatch(new PaymentSourceRemoved());
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, 'CURRENT_OFFERS.PAYMENT.REMOVE.ERROR');
        return EMPTY;
      }),
    );
  }

  addPaymentSourceDialog(replace: boolean): Observable<PaymentSource | null> {
    return this.dialog.open<AddPaymentDialogComponent, AddPaymentDialogComponentData>(AddPaymentDialogComponent, {
      minWidth: 500,
      data: { service: this, replace },
    }).afterClosed();
  }

  setPaymentSource(sourceId: string, replace: boolean): Observable<PaymentSource> {
    return (replace ? this.fsCustomerService.removePaymentSource(this.orgaService.customerId) : of(null)).pipe(
      mergeMap(() => this.fsCustomerService.setPaymentSource(this.orgaService.customerId, { id: sourceId }).pipe(
        catchError((response: FsErrorResponse) => {
          this.alert.apiError(response.error, 'CURRENT_OFFERS.PAYMENT.ADD.ERROR');
          this.store.dispatch(new PaymentSourceRemoved());
          return throwError(response);
        }),
      )),
      tap((paymentSource: PaymentSource) => {
        this.store.dispatch(new PaymentSourceAdded({ paymentSource }));
      }),
    );
  }

  updateShippingDetailsDialog(customer: Customer): Observable<ShippingDetails | null> {
    return this.dialog.open<UpdateShippingInfosDialogComponent, UpdateShippingInfosDialogComponentData>(UpdateShippingInfosDialogComponent, {
      minWidth: '400px',
      maxWidth: '600px',
      data: {
        infos: customer.shipping,
        service: this,
      },
    }).afterClosed();
  }

  setCustomerShippingDetails(shippingDetails: ShippingDetails): Observable<Customer> {
    return this.fsCustomerService.setCustomerShippingDetails(this.orgaService.customerId, shippingDetails).pipe(
      tap((updatedCustomer: Customer) => {
        this.alert.success('CURRENT_OFFERS.PAYMENT.INFOS.UPDATE.SUCCESS');
        this.store.dispatch(new ShippingDetailsUpdated({ updatedCustomer }));
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, 'CURRENT_OFFERS.PAYMENT.INFOS.UPDATE.ERROR');
        return EMPTY;
      }),
    );
  }
}
